import { SkFont } from "@shopify/react-native-skia";
import React from "react";
import { Platform } from "react-native";
import { CartesianChart, Bar } from "victory-native";

// Platform-guarded require: victory (web SVG) crashes on native
// because it registers SVG elements like <line> that don't exist in RN
const { VictoryChart, VictoryBar, VictoryAxis } =
  Platform.OS === "web" ? require("victory") : {};

/**
 * Satoshi font family for consistent design system styling
 */
const SATOSHI_FONT_FAMILY = "Satoshi, sans-serif";

/* design-system: dynamic-style waiver — Victory chart library style API (not RN/NativeWind style prop); data-driven label colors */
const victoryAxisStyle = (labelColor?: string) =>
  ({
    tickLabels: {
      fill: labelColor || "#666",
      fontSize: 10,
      fontFamily: SATOSHI_FONT_FAMILY,
    },
    axis: { stroke: "transparent" },
    ticks: { stroke: "transparent" },
  }) as const;

/* design-system: dynamic-style waiver — Victory chart library style API (not RN/NativeWind style prop); data-driven bar fill color */
const victoryBarStyle = (fillColor: string) =>
  ({ data: { fill: fillColor } }) as const;

/**
 * Data structure for PlatformBarChart
 */
export interface PlatformBarChartData {
  [key: string]: string | number;
}

/**
 * Props for PlatformBarChartView component
 */
export interface PlatformBarChartViewProps {
  /** Array of data objects to be displayed in the chart */
  data: PlatformBarChartData[];
  /** Key name for x-axis values */
  xKey: string;
  /** Key name for y-axis values */
  yKey: string;
  /** Color for the bars */
  color: string;
  /** Skia font for labels (native only) */
  font?: SkFont | null;
  /** Chart width in pixels */
  width: number;
  /** Chart height in pixels */
  height: number;
  /** Padding between domain and chart edge */
  domainPadding: number;
  /** Padding around the chart */
  padding: number;
  /** X-axis configuration */
  xAxis?: {
    labelColor?: string;
    tickCount?: number;
  };
  /** Y-axis configuration */
  yAxis?: {
    labelColor?: string;
  }[];
  /** Width of individual bars */
  barWidth: number;
  /** Whether to show value labels on bars */
  showLabels: boolean;
}

/**
 * Platform-aware bar chart view component that uses:
 * - Victory (DOM/SVG) on web for reliability
 * - victory-native (Skia) on mobile for performance
 * @param root0 - Component props
 * @param root0.data - Array of data objects containing x and y values for bars
 * @param root0.xKey - Property name to use for x-axis values from data objects
 * @param root0.yKey - Property name to use for y-axis values from data objects
 * @param root0.color - Hex or named color string for bar fill color
 * @param root0.font - Skia font object for rendering labels on native platforms
 * @param root0.width - Chart width in pixels
 * @param root0.height - Chart height in pixels
 * @param root0.domainPadding - Padding in pixels between data domain and chart edges
 * @param root0.padding - Padding in pixels around entire chart area
 * @param root0.xAxis - Configuration object for x-axis styling and tick formatting
 * @param root0.yAxis - Array of configuration objects for y-axis styling
 * @param root0.barWidth - Width in pixels of individual bar elements
 * @param root0.showLabels - Whether to display value labels on top of bars
 * @remarks
 * This is a pure presentation component. All props are required from the Container.
 */
const PlatformBarChartView = ({
  data,
  xKey,
  yKey,
  color,
  font,
  width,
  height,
  domainPadding,
  padding,
  xAxis,
  yAxis,
  barWidth,
  showLabels,
}: PlatformBarChartViewProps): React.ReactElement =>
  Platform.OS === "web" ? (
    <VictoryChart
      width={width}
      height={height}
      domainPadding={domainPadding}
      padding={padding}
    >
      <VictoryAxis
        dependentAxis={false}
        // eslint-disable-next-line no-restricted-syntax -- Victory chart library style API (tickLabels/axis/ticks descriptors), not an RN/NativeWind style prop; non-atomizable
        style={victoryAxisStyle(xAxis?.labelColor)}
        {...(xAxis?.tickCount ? { tickCount: xAxis.tickCount } : {})}
      />
      <VictoryAxis
        dependentAxis
        // eslint-disable-next-line no-restricted-syntax -- Victory chart library style API (tickLabels/axis/ticks descriptors), not an RN/NativeWind style prop; non-atomizable
        style={victoryAxisStyle(yAxis?.[0]?.labelColor)}
      />
      <VictoryBar
        data={data}
        x={xKey}
        y={yKey}
        // eslint-disable-next-line no-restricted-syntax -- Victory chart library style API ({ data: { fill } }), not an RN/NativeWind style prop; non-atomizable
        style={victoryBarStyle(color)}
        barWidth={barWidth}
      />
    </VictoryChart>
  ) : (
    <CartesianChart
      data={data}
      xKey={xKey}
      yKeys={[yKey] as never[]}
      domainPadding={domainPadding}
      padding={padding}
      xAxis={{
        ...(font ? { font } : {}),
        labelColor: xAxis?.labelColor || "#666",
        lineColor: "transparent",
        lineWidth: 0,
        ...(xAxis?.tickCount ? { tickCount: xAxis.tickCount } : {}),
      }}
      yAxis={[
        {
          ...(font ? { font } : {}),
          labelColor: yAxis?.[0]?.labelColor || "#666",
          lineColor: "transparent",
          lineWidth: 0,
        },
      ]}
    >
      {({ points, chartBounds }) => {
        if (!points || !chartBounds) return null;
        const pointsForKey = (points as Record<string, unknown>)[yKey];
        if (!pointsForKey || !Array.isArray(pointsForKey)) return null;

        return (
          <Bar
            chartBounds={chartBounds}
            points={pointsForKey}
            barWidth={barWidth}
            roundedCorners={{ topLeft: 4, topRight: 4 }}
            color={color}
            {...(showLabels && font
              ? {
                  labels: {
                    position: "top" as const,
                    font,
                    color: xAxis?.labelColor || "#666",
                  },
                }
              : {})}
          />
        );
      }}
    </CartesianChart>
  );

PlatformBarChartView.displayName = "PlatformBarChartView";

export default React.memo(PlatformBarChartView);
